import React, { useState, useRef } from "react";
import { Button, Spinner, ProgressBar } from "react-bootstrap";
import OmrService from "../../../_services/OmrService";

interface Props {
  omrTestId: number;
  onRefresh: () => void;
}

const UploadScansTab: React.FC<Props> = ({ omrTestId, onRefresh }) => {
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (selected) {
      setFiles(Array.from(selected));
      setMessage(null);
    }
  };

  const handleUpload = async () => {
    if (files.length === 0) return;
    try {
      setUploading(true);
      setProgress(0);

      // Upload in batches of 10
      const batchSize = 10;
      let uploaded = 0;

      for (let i = 0; i < files.length; i += batchSize) {
        const batch = files.slice(i, i + batchSize);
        await OmrService.uploadScans(omrTestId, batch);
        uploaded += batch.length;
        setProgress(Math.round((uploaded / files.length) * 100));
      }

      setMessage(`${uploaded} scan(s) uploaded successfully`);
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      onRefresh();
    } catch (err: any) {
      setMessage("Upload failed: " + (err.message || ""));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      {message && (
        <div style={{
          padding: "6px 12px", borderRadius: 4, marginBottom: 12,
          background: message.includes("failed") ? "#f8d7da" : "#d4edda",
          color: message.includes("failed") ? "#721c24" : "#155724",
        }}>
          {message}
        </div>
      )}

      <div style={{
        border: "2px dashed var(--border-strong)", borderRadius: 8, padding: 30, textAlign: "center",
        marginBottom: 16, background: "var(--surface-page)",
      }}>
        <div style={{ marginBottom: 12, color: "var(--text-muted)" }}>
          Select scanned OMR sheet images (JPEG, PNG)
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileSelect}
          style={{ display: "none" }}
        />
        <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()}>
          Choose Files
        </Button>
        {files.length > 0 && (
          <div style={{ marginTop: 8, fontSize: "0.85rem" }}>
            {files.length} file(s) selected
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div>
          <div style={{ maxHeight: 200, overflow: "auto", marginBottom: 12, fontSize: "0.8rem" }}>
            {files.map((f, i) => (
              <div key={i} style={{ padding: "2px 0", borderBottom: "1px solid var(--border-subtle)" }}>
                {f.name} ({(f.size / 1024).toFixed(0)} KB)
              </div>
            ))}
          </div>

          {uploading && <ProgressBar now={progress} label={`${progress}%`} style={{ marginBottom: 12 }} />}

          <Button variant="primary" size="sm" onClick={handleUpload} disabled={uploading}>
            {uploading ? <><Spinner animation="border" size="sm" /> Uploading...</> : `Upload ${files.length} File(s)`}
          </Button>
        </div>
      )}
    </div>
  );
};

export default UploadScansTab;
